import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  ArrowLeft, Volume2, RotateCcw, BookOpen, Brain, 
  CheckCircle, XCircle, Zap, Trophy, Calendar,
  TrendingUp, Flame
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { selectMaleVoice, preloadVoices } from '@/services/voiceService';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FlashcardsPage = () => {
  const navigate = useNavigate();
  const [words, setWords] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [stats, setStats] = useState(null);
  const [sessionComplete, setSessionComplete] = useState(false);
  const [sessionStats, setSessionStats] = useState({ correct: 0, incorrect: 0, reviewed: 0 });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const synthRef = useRef(window.speechSynthesis);

  useEffect(() => {
    fetchWordsForReview();
    // Preload voices for TTS
    preloadVoices();
  }, []);

  const fetchWordsForReview = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/vocabulary/review`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWords(response.data.words || []);
      setStats(response.data.stats || null);
    } catch (error) {
      toast.error('Erro ao carregar palavras para revisão');
    } finally {
      setLoading(false);
    }
  };

  const getLanguageCode = (language) => {
    const codes = {
      'English': 'en-US',
      'Spanish': 'es-ES',
      'Portuguese': 'pt-BR',
      'French': 'fr-FR',
      'German': 'de-DE',
      'Chinese': 'zh-CN'
    };
    return codes[language] || 'en-US';
  };

  const speakWord = () => {
    if (words.length === 0) return;
    synthRef.current.cancel();
    const word = words[currentIndex];
    let textToSpeak = word.word;
    
    // REGRA GLOBAL: Falar APENAS no idioma estudado, NUNCA a tradução
    // Remove translation in parentheses (Portuguese translations)
    textToSpeak = textToSpeak.replace(/\([^)]*(?:Oi|Olá|E aí|Como|Que|Sobre|Você|Eu|Muito|Tudo|Legal|Bom|Boa|Isso)[^)]*\)/gi, '');
    textToSpeak = textToSpeak.replace(/\n*\(?(?:Tradução|PORTUGUÊS|PORTUGUESE|PT)[:\s]*[^)\n]+\)?/gi, '');
    
    // For Chinese: extract only Chinese text
    if (word.language === 'Chinese' && word.word.includes('CHINESE:')) {
      const chineseMatch = word.word.match(/CHINESE:\s*([\s\S]*?)(?=\n\n(?:PINYIN|PORTUGUÊS|PORTUGUESE)|$)/i);
      if (chineseMatch && chineseMatch[1]) {
        textToSpeak = chineseMatch[1].trim();
      }
    }
    
    textToSpeak = textToSpeak.trim();
    if (!textToSpeak) return;
    
    const utterance = new SpeechSynthesisUtterance(textToSpeak);
    
    // Use centralized voiceService for consistent male voice in flashcards
    const selectedVoice = selectMaleVoice(word.language);
    if (selectedVoice) {
      utterance.voice = selectedVoice;
      utterance.lang = selectedVoice.lang;
      console.log(`🎤 Flashcards: Using male voice "${selectedVoice.name}"`);
    } else {
      utterance.lang = getLanguageCode(word.language);
    }
    
    utterance.rate = 0.92;  // Natural conversational pace
    utterance.pitch = 0.88; // Natural male voice (not too deep)
    utterance.onstart = () => setIsPlaying(true);
    utterance.onend = () => setIsPlaying(false);
    utterance.onerror = () => setIsPlaying(false);
    synthRef.current.speak(utterance);
  };

  const submitReview = async (quality) => {
    if (isSubmitting || words.length === 0) return;
    
    setIsSubmitting(true);
    const currentWord = words[currentIndex];
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/vocabulary/review`, {
        word_id: currentWord.id,
        quality: quality
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Update session stats
      setSessionStats(prev => ({
        ...prev,
        reviewed: prev.reviewed + 1,
        correct: quality >= 3 ? prev.correct + 1 : prev.correct,
        incorrect: quality < 3 ? prev.incorrect + 1 : prev.incorrect
      }));

      // Show feedback toast
      if (quality >= 4) {
        toast.success(`Próxima revisão: ${response.data.next_review_text}`);
      } else if (quality === 3) {
        toast.info(`Revisão em breve: ${response.data.next_review_text}`);
      } else {
        toast.warning('Vamos revisar novamente em breve!');
      }

      // Move to next card or end session
      if (currentIndex < words.length - 1) {
        setIsFlipped(false);
        setTimeout(() => setCurrentIndex(prev => prev + 1), 300);
      } else {
        setSessionComplete(true);
      }
    } catch (error) {
      toast.error('Erro ao salvar revisão');
    } finally {
      setIsSubmitting(false);
    }
  };

  const flipCard = () => {
    setIsFlipped(!isFlipped);
    if (!isFlipped) {
      // Auto-play audio when revealing answer
      setTimeout(speakWord, 300);
    }
  };

  const restartSession = () => {
    setSessionComplete(false);
    setSessionStats({ correct: 0, incorrect: 0, reviewed: 0 });
    setCurrentIndex(0);
    setIsFlipped(false);
    fetchWordsForReview();
  };

  const currentWord = words[currentIndex];

  // Quality buttons with SM-2 mapping
  const qualityButtons = [
    { quality: 0, label: 'Esqueci', icon: XCircle, color: 'bg-red-500 hover:bg-red-600', desc: 'Não lembrei' },
    { quality: 3, label: 'Difícil', icon: Brain, color: 'bg-orange-500 hover:bg-orange-600', desc: 'Demorei para lembrar' },
    { quality: 4, label: 'Bom', icon: CheckCircle, color: 'bg-emerald-500 hover:bg-emerald-600', desc: 'Lembrei bem' },
    { quality: 5, label: 'Fácil', icon: Zap, color: 'bg-lime text-forest hover:bg-lime/90', desc: 'Lembrei instantaneamente' },
  ];

  return (
    <div className="min-h-screen bg-sand" data-testid="flashcards-page">
      {/* Header */}
      <header className="bg-white shadow-soft sticky top-0 z-10">
        <div className="container mx-auto max-w-4xl px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 sm:gap-4">
              <Button
                onClick={() => navigate('/dashboard')}
                variant="ghost"
                size="sm"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-lime to-emerald-400 rounded-full flex items-center justify-center">
                  <Brain className="w-5 h-5 text-forest" />
                </div>
                <div>
                  <h1 className="text-lg sm:text-xl font-semibold text-forest">Repetição Espaçada</h1>
                  <p className="text-xs sm:text-sm text-stone">
                    {stats ? `${stats.due_today} palavras para hoje` : 'Carregando...'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto max-w-4xl px-4 sm:px-6 py-6 sm:py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-pulse text-forest">Carregando...</div>
          </div>
        ) : sessionComplete ? (
          // Session Complete Screen
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-10 sm:py-16"
          >
            <div className="w-24 h-24 bg-lime/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <Trophy className="w-12 h-12 text-forest" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-forest mb-2">Sessão Completa!</h2>
            <p className="text-stone mb-8">Ótimo trabalho revisando seu vocabulário</p>
            
            {/* Session Stats */}
            <div className="grid grid-cols-3 gap-4 max-w-md mx-auto mb-8">
              <div className="bg-white rounded-2xl p-4 shadow-soft">
                <p className="text-2xl font-bold text-forest">{sessionStats.reviewed}</p>
                <p className="text-xs text-stone">Revisadas</p>
              </div>
              <div className="bg-white rounded-2xl p-4 shadow-soft">
                <p className="text-2xl font-bold text-emerald-600">{sessionStats.correct}</p>
                <p className="text-xs text-stone">Acertos</p>
              </div>
              <div className="bg-white rounded-2xl p-4 shadow-soft">
                <p className="text-2xl font-bold text-orange-500">{sessionStats.incorrect}</p>
                <p className="text-xs text-stone">Para revisar</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                onClick={restartSession}
                className="bg-forest text-white hover:bg-forest/90 gap-2"
                size="lg"
              >
                <RotateCcw className="w-4 h-4" />
                Nova Sessão
              </Button>
              <Button
                onClick={() => navigate('/dashboard')}
                variant="outline"
                size="lg"
              >
                Voltar ao Início
              </Button>
            </div>
          </motion.div>
        ) : words.length === 0 ? (
          // No Words Due
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-16 sm:py-20"
          >
            <div className="w-20 h-20 bg-lime/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-emerald-500" />
            </div>
            <h2 className="text-xl font-medium text-forest mb-2">Tudo em dia!</h2>
            <p className="text-stone mb-6 max-w-md mx-auto">
              {stats && stats.total_words > 0 
                ? `Você tem ${stats.total_words} palavras. Volte mais tarde para revisar!`
                : 'Salve palavras durante as conversas para começar a revisar.'
              }
            </p>
            
            {/* Stats Cards */}
            {stats && stats.total_words > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-lg mx-auto mb-8">
                <div className="bg-white rounded-xl p-3 shadow-soft">
                  <p className="text-lg font-bold text-blue-500">{stats.words_new}</p>
                  <p className="text-xs text-stone">Novas</p>
                </div>
                <div className="bg-white rounded-xl p-3 shadow-soft">
                  <p className="text-lg font-bold text-orange-500">{stats.words_learning}</p>
                  <p className="text-xs text-stone">Aprendendo</p>
                </div>
                <div className="bg-white rounded-xl p-3 shadow-soft">
                  <p className="text-lg font-bold text-emerald-500">{stats.words_review}</p>
                  <p className="text-xs text-stone">Revisão</p>
                </div>
                <div className="bg-white rounded-xl p-3 shadow-soft">
                  <p className="text-lg font-bold text-forest">{stats.words_mastered}</p>
                  <p className="text-xs text-stone">Dominadas</p>
                </div>
              </div>
            )}

            <Button
              onClick={() => navigate('/dashboard')}
              className="bg-forest hover:bg-forest/90 text-sand"
            >
              Iniciar Conversa
            </Button>
          </motion.div>
        ) : (
          // Review Mode
          <div className="flex flex-col items-center">
            {/* Progress & Stats Bar */}
            <div className="w-full max-w-md mb-6">
              <div className="flex items-center justify-between text-sm text-stone mb-2">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <span>Hoje: {currentIndex + 1} / {words.length}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="flex items-center gap-1 text-emerald-600">
                    <CheckCircle className="w-3 h-3" /> {sessionStats.correct}
                  </span>
                  <span className="flex items-center gap-1 text-orange-500">
                    <XCircle className="w-3 h-3" /> {sessionStats.incorrect}
                  </span>
                </div>
              </div>
              <div className="h-2 bg-forest/10 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-lime to-emerald-500"
                  initial={{ width: 0 }}
                  animate={{ width: `${((currentIndex + 1) / words.length) * 100}%` }}
                  transition={{ duration: 0.3 }}
                />
              </div>
            </div>

            {/* Word Status Badge */}
            {currentWord && (
              <div className="mb-4">
                {currentWord.repetitions === 0 ? (
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                    Nova palavra
                  </span>
                ) : currentWord.interval < 7 ? (
                  <span className="px-3 py-1 bg-orange-100 text-orange-700 text-xs font-medium rounded-full">
                    Aprendendo • {currentWord.repetitions} revisões
                  </span>
                ) : currentWord.interval < 30 ? (
                  <span className="px-3 py-1 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full">
                    Revisão • Intervalo: {currentWord.interval} dias
                  </span>
                ) : (
                  <span className="px-3 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded-full flex items-center gap-1">
                    <Flame className="w-3 h-3" /> Dominada
                  </span>
                )}
              </div>
            )}

            {/* Flashcard */}
            <div className="w-full max-w-md perspective-1000">
              <motion.div
                className="relative w-full aspect-[4/3] cursor-pointer"
                onClick={flipCard}
                data-testid="flashcard"
              >
                <AnimatePresence mode="wait">
                  <motion.div
                    key={`${currentIndex}-${isFlipped}`}
                    initial={{ rotateY: isFlipped ? -90 : 90, opacity: 0 }}
                    animate={{ rotateY: 0, opacity: 1 }}
                    exit={{ rotateY: isFlipped ? 90 : -90, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className={`absolute inset-0 rounded-3xl shadow-medium flex flex-col items-center justify-center p-6 sm:p-8 ${
                      isFlipped 
                        ? 'bg-gradient-to-br from-lime to-emerald-400' 
                        : 'bg-white'
                    }`}
                  >
                    {!isFlipped ? (
                      <>
                        <span className="text-xs uppercase tracking-wider text-stone mb-4">
                          {currentWord?.language}
                        </span>
                        <h2 className="text-2xl sm:text-3xl font-semibold text-forest text-center leading-relaxed">
                          {currentWord?.word}
                        </h2>
                        <p className="text-sm text-stone mt-6">
                          Toque para revelar
                        </p>
                      </>
                    ) : (
                      <>
                        <span className="text-xs uppercase tracking-wider text-forest/70 mb-4">
                          Tradução
                        </span>
                        <h2 className="text-2xl sm:text-3xl font-semibold text-forest text-center leading-relaxed">
                          {currentWord?.translation}
                        </h2>
                        <Button
                          onClick={(e) => { e.stopPropagation(); speakWord(); }}
                          variant="ghost"
                          size="sm"
                          className="mt-4 gap-2"
                        >
                          <Volume2 className={`w-4 h-4 ${isPlaying ? 'animate-pulse' : ''}`} />
                          Ouvir
                        </Button>
                      </>
                    )}
                  </motion.div>
                </AnimatePresence>
              </motion.div>
            </div>

            {/* Quality Buttons - Only show when flipped */}
            <AnimatePresence>
              {isFlipped && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  className="w-full max-w-md mt-6"
                >
                  <p className="text-center text-sm text-stone mb-3">Como foi sua lembrança?</p>
                  <div className="grid grid-cols-4 gap-2">
                    {qualityButtons.map((btn) => {
                      const Icon = btn.icon;
                      return (
                        <button
                          key={btn.quality}
                          onClick={() => submitReview(btn.quality)}
                          disabled={isSubmitting}
                          className={`${btn.color} text-white rounded-xl p-3 sm:p-4 transition-all transform hover:scale-105 active:scale-95 disabled:opacity-50 flex flex-col items-center gap-1`}
                          data-testid={`quality-${btn.quality}`}
                        >
                          <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
                          <span className="text-xs font-medium">{btn.label}</span>
                        </button>
                      );
                    })}
                  </div>
                  <div className="grid grid-cols-4 gap-2 mt-1">
                    {qualityButtons.map((btn) => (
                      <p key={btn.quality} className="text-[10px] text-stone text-center">
                        {btn.desc}
                      </p>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Tip when not flipped */}
            {!isFlipped && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="mt-8 text-center"
              >
                <div className="flex items-center justify-center gap-2 text-stone text-sm">
                  <TrendingUp className="w-4 h-4" />
                  <span>Pense na resposta antes de revelar</span>
                </div>
              </motion.div>
            )}

            {/* SRS Info Box */}
            <div className="mt-8 w-full max-w-md bg-white/50 rounded-2xl p-4 border border-forest/10">
              <h4 className="font-medium text-forest text-sm mb-2 flex items-center gap-2">
                <Brain className="w-4 h-4" />
                Como funciona a Repetição Espaçada?
              </h4>
              <p className="text-xs text-stone leading-relaxed">
                O algoritmo SM-2 ajusta automaticamente o intervalo de revisão com base no seu desempenho. 
                Palavras fáceis aparecem menos, palavras difíceis aparecem mais frequentemente. 
                Isso otimiza sua memorização de longo prazo!
              </p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default FlashcardsPage;
